/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import React, { createContext, useCallback, useEffect, useState } from 'react';

/**
 * The user context provider.
 * @param {object} props The events provider props.
 * @returns {JSX.Element} Event context provider component.
 */
export const UserProvider = ({ children, userService, userId }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [user, setUser] = useState([]);

    // Load user object from adapter and update internal state with fetched object.
    useEffect(() => {
        setIsLoading(true);

        userService.get(userId).then((response) => {
            setUser(response);
            setIsLoading(false);
            setIsReady(true);
        });
    }, [user]);

    // Calls adapter method in order to update user with updated values.
    const updateUser = useCallback(
        (id, newUser) => {
            setIsLoading(true);

            return userService.setAll(newUser, id).then((result) => {
                setUser(result);
                setIsLoading(false);
                setIsReady(true);
            });
        },
        [user]
    );

    // Calls adapter method in order to update user push notifications preferences.
    const toggleNotifications = useCallback(
        (toggle) => {
            setIsLoading(true);

            return userService.toggleNotifications(toggle).then((result) => {
                setUser(result);
                setIsLoading(false);
                setIsReady(true);
            });
        },
        [user]
    );

    // Calls adapter method in order to update user notifications preferences.
    const updateNotifications = useCallback(
        (notifications) => {
            setIsLoading(true);

            return userService
                .updateNotifications(notifications, user)
                .then((result) => {
                    setUser(result);
                    setIsLoading(false);
                    setIsReady(true);
                });
        },
        [user]
    );

    return (
        <UserContext.Provider
            value={{
                user,
                userService,
                isLoading,
                isReady,
                updateUser,
                updateNotifications,
                toggleNotifications,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};

/**
 * The context for user.
 * @export
 */
export const UserContext = createContext({
    user: {},
    userId: null,
    userService: null,
    isLoading: false,
    isReady: false,
    updateUser: () => {},
    updateNotifications: () => {},
    toggleNotifications: () => {},
});
